/**
 * Subscriptions Domain - Composition Root
 *
 * Single entry point for all domain dependencies.
 * Implements singleton pattern with explicit initialization.
 *
 * Flow:
 * 1. App/bootstrap.ts calls initSubscriptionsComposition() when the user is authenticated
 * 2. Hooks call getSubscriptionBlockUseCases(), getPaymentMethodUseCases(), etc.
 * 3. Use cases are already composed with their dependencies
 *
 * Three axios clients live side-by-side inside the composition:
 *  - `subscriptionsApi` (parrot-subscriptions-api) for the migrated endpoints
 *  - `parrotApi` (legacy) for fiscal-regimes routes
 *  - `onboardingApi` (parrot-onboarding-api) for CSF extraction
 *
 * @module @subscriptions/composition
 */

import type { AuthService } from 'types/AuthService';
import { onboardingApi, parrotApi, subscriptionsApi } from '@subscriptions/infrastructure/api/axiosSubscriptions';
import type {
  SubscriptionsComposition,
  SubscriptionBlockUseCases,
  PaymentMethodUseCases,
  PaymentHistoryUseCases,
  InvoiceDataUseCases,
  SubscriptionEventsUseCases,
  SessionUseCases,
} from './types';
import { createSubscriptionBlockUseCases } from './factories/createSubscriptionBlockUseCases';
import { createPaymentMethodUseCases } from './factories/createPaymentMethodUseCases';
import { createPaymentHistoryUseCases } from './factories/createPaymentHistoryUseCases';
import { createInvoiceDataUseCases } from './factories/createInvoiceDataUseCases';
import { createSubscriptionEventsUseCases } from './factories/createSubscriptionEventsUseCases';
import { createSessionUseCases } from './factories/createSessionUseCases';

export type {
  SubscriptionsComposition,
  SubscriptionBlockUseCases,
  PaymentMethodUseCases,
  PaymentHistoryUseCases,
  InvoiceDataUseCases,
  SubscriptionEventsUseCases,
  SessionUseCases,
} from './types';

const createSubscriptionsComposition = (authService: AuthService): SubscriptionsComposition => ({
  subscriptionBlockUseCases: createSubscriptionBlockUseCases(subscriptionsApi),
  paymentMethodUseCases: createPaymentMethodUseCases(subscriptionsApi),
  paymentHistoryUseCases: createPaymentHistoryUseCases(subscriptionsApi),
  invoiceDataUseCases: createInvoiceDataUseCases(subscriptionsApi, parrotApi, onboardingApi, authService),
  subscriptionEventsUseCases: createSubscriptionEventsUseCases(subscriptionsApi),
  sessionUseCases: createSessionUseCases(authService),
});

let composition: SubscriptionsComposition | null = null;

/**
 * Initializes the Subscriptions domain Composition Root.
 * MUST be called ONCE during app bootstrap, BEFORE any hook attempts to use the use cases.
 *
 * @param authService - Authentication service from useAuth hook (used by legacy flows)
 */
export const initSubscriptionsComposition = (authService: AuthService): void => {
  if (composition !== null) {
    return;
  }
  composition = createSubscriptionsComposition(authService);
};

/**
 * Gets the Composition Root singleton.
 * MUST be called AFTER initSubscriptionsComposition.
 *
 * @throws Error if not initialized
 */
export const getSubscriptionsComposition = (): SubscriptionsComposition => {
  if (composition === null) {
    throw new Error(
      '[Subscriptions] Composition not initialized.\n' +
        'Call initSubscriptionsComposition(authService) in App/bootstrap.ts before using hooks.',
    );
  }
  return composition;
};

/**
 * Reset for testing.
 *
 * @internal
 */
export const resetSubscriptionsComposition = (): void => {
  composition = null;
};

/** Gets subscription block use cases */
export const getSubscriptionBlockUseCases = (): SubscriptionBlockUseCases => {
  return getSubscriptionsComposition().subscriptionBlockUseCases;
};

/** Gets payment method use cases */
export const getPaymentMethodUseCases = (): PaymentMethodUseCases => {
  return getSubscriptionsComposition().paymentMethodUseCases;
};

/** Gets payment history use cases */
export const getPaymentHistoryUseCases = (): PaymentHistoryUseCases => {
  return getSubscriptionsComposition().paymentHistoryUseCases;
};

/** Gets invoice data use cases */
export const getInvoiceDataUseCases = (): InvoiceDataUseCases => {
  return getSubscriptionsComposition().invoiceDataUseCases;
};

/** Gets collection-status event use cases */
export const getSubscriptionEventsUseCases = (): SubscriptionEventsUseCases => {
  return getSubscriptionsComposition().subscriptionEventsUseCases;
};

/** Gets the use cases that read the signed-in session */
export const getSessionUseCases = (): SessionUseCases => {
  return getSubscriptionsComposition().sessionUseCases;
};
